import { style } from 'typestyle'

/* Styles ======================================================================================= */
import { StyleClass } from '../style-class'

/* Types ======================================================================================== */
import { BoxProps } from './box.component'

type ClassNames = {
  component: string
  /**
   * Base class for box
   */
  base: string

  flexed?: string

  /**
   * Themed class for box
   */
  themed: string

  /**
   * Boxed class for box
   */
  boxed: string
}

type ClassProps = Omit<BoxProps, 'style' | 'children' | 'className'>

const getNames: (props: ClassProps, theme: any) => ClassNames = (props, theme) => {
  const {
    alt,
    baseline = 'none',
    fullWidth,
    fullHeight,
    halfWidth,
    halfHeight,
    border,
    borderContrast = 'medium',
    shadow,
    shadowContrast = 'medium',
    rounded,
    centerX,
    centerY,
    centerAbsoluteX,
    centerAbsoluteY,
    alignLeft,
    alignRight,
    alignCenterX,
    alignCenterY,
    alignBottom,
    alignTop,
    spaceBetween,
    maxWidth,
    minWidth,
    maxHeight,
    minHeight = props.fullHeight ? 'inherit' : null,
    row,
    column,
    flex,
    flexWrap,
    relative,
    absolute,
    fixed,
    sticky,
    scroll,
    onlyContrast,
    farBottom,
    farLeft,
    farRight,
    farTop,
    centerAutoX,
    centerAutoY,
    backgroundImage,
  } = props

  const component = style({
    display: 'block',
  })

  const base = style()

  const flexStyleObject: any = {}

  if (flex) {
    flexStyleObject.display = 'flex'
  }

  if (row) {
    flexStyleObject.display = 'flex'
    flexStyleObject.flexDirection = 'column'
  }

  if (column) {
    flexStyleObject.display = 'flex'
    flexStyleObject.flexDirection = 'row'
  }

  const flexed = style(flexStyleObject)

  const themed = style({
    background: baseline !== 'none' && theme.getBaseline(baseline, alt).color.light,
    color: theme.getBaseline(baseline, alt).contrast.medium,
    transitionDuration: '0.1s',
    transitionTimingFunction: 'ease-in-out',
    transitionProperty: 'background',
  })

  const centeredStyle: any = {}
  const backgroundStyle: any = {}

  if (centerX) {
    centeredStyle.marginTop = 0
    centeredStyle.marginBottom = 0
    centeredStyle.marginLeft = 'auto'
    centeredStyle.marginRight = 'auto'
  }

  if (centerY) {
    centeredStyle.marginTop = 'auto'
    centeredStyle.marginBottom = 'auto'
  }

  if (centerAbsoluteX) {
    centeredStyle.position = 'absolute'
    centeredStyle.left = '50%'
    centeredStyle.right = '50%'
    centeredStyle.transform = 'translateX(-50%)'
  }

  if (centerAbsoluteY) {
    centeredStyle.position = 'absolute'
    centeredStyle.top = '50%'
    centeredStyle.bottom = '50%'
    if (centeredStyle.transform) {
      centeredStyle.transform += ' translateY(-50%)'
    }
  }

  if (alignCenterY) {
    centeredStyle.display = 'flex'

    if (row) {
      centeredStyle.justifyContent = 'center'
    } else {
      centeredStyle.alignItems = 'center'
    }
  }

  if (alignCenterX) {
    centeredStyle.display = 'flex'
    if (row) {
      centeredStyle.alignItems = 'center'
    } else {
      centeredStyle.justifyContent = 'center'
    }
  }

  if (alignLeft) {
    centeredStyle.display = 'flex'
    centeredStyle.justifyContent = 'flex-start'
  }

  if (alignRight) {
    centeredStyle.display = 'flex'

    if (row) {
      centeredStyle.alignItems = 'flex-end'
    } else {
      centeredStyle.justifyContent = 'flex-end'
    }
  }

  if (spaceBetween) {
    centeredStyle.display = 'flex'
    centeredStyle.justifyContent = 'space-between'
  }

  if (alignTop) {
    centeredStyle.display = 'flex'
    centeredStyle.alignItems = 'flex-start'
  }

  if (alignBottom) {
    centeredStyle.display = 'flex'
    centeredStyle.alignItems = 'flex-end'
  }

  if (farRight) {
    centeredStyle.right = 0
  }

  if (farBottom) {
    centeredStyle.bottom = 0
  }

  if (farLeft) {
    centeredStyle.left = 0
  }

  if (farTop) {
    centeredStyle.top = 0
  }

  if (centerAutoX) {
    centeredStyle.margin = '0 auto'
  }

  if (centerAutoY) {
    centeredStyle.margin = 'auto 0'
  }

  let position = ''

  if (relative) {
    position = 'relative'
  }

  if (absolute) {
    position = 'absolute'
  }

  if (fixed) {
    position = 'fixed'
  }

  if (sticky) {
    position = 'sticky'
  }

  if (backgroundImage) {
    backgroundStyle.background = `url("${backgroundImage}")`
    backgroundStyle.backgroundSize = 'cover'
    backgroundStyle.backgroundRepeat = 'no-repeat'
    backgroundStyle.backgroundPosition = 'center'
  }

  let width
  if (fullWidth) {
    width = '100%'
  } else if (halfWidth) {
    width = '50%'
  }

  let height
  if (fullHeight) {
    height = '100%'
  } else if (halfHeight) {
    height = '50%'
  }

  const boxed = style({
    width,
    height,
    boxShadow: shadow ? `1px 1px 40px -1px ${theme.boxes.shadow[shadowContrast]}` : '',
    border: border ? `1px solid ${theme.boxes.border[borderContrast]}` : '',
    borderRadius: rounded ? 4 : 0,
    overflow: scroll ? 'auto' : '',
    maxWidth,
    minWidth,
    maxHeight,
    minHeight,
    position,
    flexWrap: flexWrap ? 'wrap' : '',
    ...centeredStyle,
    ...backgroundStyle,
  })

  return {
    component,
    base,
    flexed,
    themed,
    boxed,
  }
}

/* Export ======================================================================================= */
export const boxClass = new StyleClass<ClassNames, ClassProps>(getNames)
